import {ModalState} from "../../../types/modals";
import {Image, PlainDetails} from "../../../types/admin";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {deleteResult, updateResults} from "../adminList";
import {changeGameModal} from "./gameEditModal";
import {changeSectionModal} from "./sectionEditModal";
import {toastSuccess} from "../admin";


export type ThemeModalState = ModalState & {
    background_image: Image | null
    logo: Image | null
    colors: any,
    theme_id: string | null
    name: string
    used_in: boolean
    delete_message: string
    game_id?: string
    section_id?: string
}

const initialState: ThemeModalState = {
    loading: true,
    isOpen: false,
    error: null,
    theme_id: null,
    background_image: null,
    logo: null,
    colors: null,
    delete_message: '',
    name: '',
    used_in: false
}


export const getTheme = createAsyncThunk(
    'themeModal/getTheme',
    async (theme_id: string, thunkAPI) => {
        try {
            return await api.get(`/API/admin/themes/${theme_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewTheme = createAsyncThunk(
    'themeModal/saveNewTheme',
    async (payload: FormData, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const game_id = payload.get('game_id');
            const section_id = payload.get('section_id');
            const response = await api.post('/API/admin/themes/', payload, true);
            if (game_id) {
                const { theme_id, name } = response;
                const themeDetails: PlainDetails = {
                    value: theme_id,
                    label: name
                }
                dispatch(changeGameModal({theme: themeDetails}));
            }
            if (section_id) {
                const { theme_id, name } = response;
                const themeDetails: PlainDetails = {
                    value: theme_id,
                    label: name
                }
                dispatch(changeSectionModal({theme: themeDetails}));
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveTheme = createAsyncThunk(
    'themeModal/saveTheme',
    async (payload: FormData, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const theme_id = payload.get('theme_id');
            if (!theme_id) {
                return thunkAPI.rejectWithValue('Theme ID is required');
            }
            const response = await api.patch(`/API/admin/themes/${payload.get('theme_id')}/`, payload, true);
            dispatch(updateResults({ result: response }));
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const deleteTheme = createAsyncThunk(
    'themeModal/deleteTheme',
    async (theme_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/themes/${theme_id}/`);
            if(response.key){
                dispatch(deleteResult({...response}));
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type ModalPayload = {
    theme_id: string | null
    game_id?: string
    section_id?: string
}

const slice = createSlice({
    name: 'themeModal',
    initialState,
    reducers: {
        closeModal: () => {
            return initialState;
        },
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            if (action.payload.theme_id) {
                state.theme_id = action.payload.theme_id;
            } else {
                state.loading = false
            } if(action.payload.game_id) {
                state.game_id = action.payload.game_id;
            }
            if (action.payload.section_id) {
                state.section_id = action.payload.section_id;
            }
            state.isOpen = true;
        },
        changeModal: (state, action: PayloadAction<Partial<ThemeModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTheme.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTheme.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.theme_id = action.payload.theme_id;
                state.background_image = action.payload.background_image;
                state.logo = action.payload.logo;
                state.colors = action.payload.colors;
                state.name = action.payload.name;
                state.used_in = action.payload.used_in;
                state.delete_message = action.payload.delete_message;
            })
            .addCase(getTheme.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewTheme.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewTheme.fulfilled, () => {
                return initialState;
            })
            .addCase(saveNewTheme.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveTheme.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveTheme.fulfilled, () => {
                return initialState;
            })
            .addCase(saveTheme.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteTheme.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTheme.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteTheme.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
    }
});

export const {
    closeModal,
    openModal,
    changeModal
} = slice.actions;

export default slice.reducer;